import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  TrendingUp, 
  AlertTriangle, 
  Search, 
  RefreshCw,
  Sparkles,
  PieChart,
  BarChart3,
  Shield,
  Flame,
  Zap,
  CheckCircle,
  XCircle,
  Clock,
  ThumbsUp,
  ThumbsDown,
  ChevronDown,
  Loader2,
  BookOpen,
  DollarSign,
  Database,
  Cpu,
  Brain,
  TrendingDown
} from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';

interface VendorSummary {
  total_transactions: number;
  vendors_detected: number;
  top_vendors: { name: string; count: number }[];
  category_distribution: Record<string, number>;
  disaster_vendors: string[];
  unknown_vendors_count: number;
  high_confidence_rate: number;
}

interface KnownVendors {
  total_known_vendors: number;
  base_vendors?: {
    disaster_restoration: number;
    cybersecurity: number;
    business: number;
  };
  expanded_vendors?: {
    [key: string]: number;
  };
  vendors: {
    disaster_restoration: any[];
    cybersecurity: any[];
    business: any[];
  };
}

interface ExtractionResult {
  vendor_name: string;
  confidence: number;
  source: string;
  is_known_vendor: boolean;
  vendor_category?: string;
  reasoning?: string;
}

interface PendingVendor {
  name: string;
  normalized_name: string;
  count: number;
  status: string;
  last_seen: string;
  sample_descriptions?: string[];
}

interface LearnedVendor {
  vendor_name: string;
  category: string;
  subcategory?: string;
  approved_by: string;
  approved_at: string;
}

interface VendorStats {
  pending_review: number;
  approved_vendors: number;
  rejected_vendors: number;
  total_discovered?: number;
}

interface TierBreakdown {
  count: number;
  percentage: number;
  description: string;
  cost: number;
}

interface ClassificationTierStats {
  tier_breakdown: {
    tier_1_local_db: TierBreakdown;
    tier_2_local_ml: TierBreakdown;
    tier_3_dual_ai: TierBreakdown;
    human_review?: { count: number; description: string };
  };
  cost_savings: {
    api_calls_avoided: number;
    cost_per_api_call: number;
    total_saved: number;
    total_incurred: number;
    savings_rate: number;
  };
  database_stats: {
    unique_vendors: number;
    categories: number;
    databases_merged?: number;
  };
  total_transactions: number;
  message: string;
}

// Category options for vendor approval
const CATEGORY_OPTIONS = [
  { value: 'Disaster Recovery', subcategories: ['Fire Restoration', 'Flood Remediation', 'Water Extraction', 'Smoke Damage', 'Mold Remediation', 'General Restoration'] },
  { value: 'Temporary Operations', subcategories: ['Temporary Location', 'Equipment Rental', 'Temporary Staffing'] },
  { value: 'Business Continuity', subcategories: ['Data Recovery', 'IT Restoration', 'Communication Setup'] },
  { value: 'Payroll & Benefits', subcategories: ['Payroll Processing', 'Benefits Administration', 'HR Services'] },
  { value: 'Technology & Software', subcategories: ['SaaS', 'Cloud Services', 'Security Software', 'Productivity Tools'] },
  { value: 'Utilities', subcategories: ['Electric', 'Gas', 'Water', 'Telecom', 'Internet'] },
  { value: 'Professional Services', subcategories: ['Accounting', 'Legal', 'Consulting', 'Marketing'] },
  { value: 'Insurance', subcategories: ['Property', 'Liability', 'Workers Comp', 'Cyber'] },
  { value: 'Food & Beverage Cost', subcategories: ['Food Distribution', 'Beverage', 'Supplies'] },
  { value: 'Shipping & Logistics', subcategories: ['Shipping', 'Freight', 'Delivery'] },
  { value: 'Equipment & Supplies', subcategories: ['Office Supplies', 'IT Equipment', 'Industrial Equipment'] },
];

export default function VendorIntelligenceDashboard() {
  const [summary, setSummary] = useState<VendorSummary | null>(null);
  const [knownVendors, setKnownVendors] = useState<KnownVendors | null>(null);
  const [loading, setLoading] = useState(true);
  const [testDescription, setTestDescription] = useState('');
  const [testResult, setTestResult] = useState<ExtractionResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'known' | 'pending' | 'learned' | 'test'>('overview');
  
  // Pending vendors state
  const [pendingVendors, setPendingVendors] = useState<PendingVendor[]>([]);
  const [vendorStats, setVendorStats] = useState<VendorStats | null>(null);
  const [learnedVendors, setLearnedVendors] = useState<LearnedVendor[]>([]);
  const [approvalLoading, setApprovalLoading] = useState<string | null>(null);
  const [tierStats, setTierStats] = useState<ClassificationTierStats | null>(null);
  
  // Approval form state
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel with individual error handling
      const results = await Promise.allSettled([
        api.get('/api/v1/vendor-intelligence/summary'),
        api.get('/api/v1/vendor-intelligence/known-vendors'),
        api.get('/api/v1/vendor-intelligence/discovered'),
        api.get('/api/v1/vendor-intelligence/learned'),
        api.get('/api/v1/vendor-intelligence/classification-tiers')
      ]);
      
      // Process results individually, using defaults for failures
      if (results[0].status === 'fulfilled') {
        setSummary(results[0].value);
      } else {
        console.error('Summary fetch failed:', results[0].reason);
        setSummary({
          total_transactions: 0,
          vendors_detected: 0,
          top_vendors: [],
          category_distribution: {},
          disaster_vendors: [],
          unknown_vendors_count: 0,
          high_confidence_rate: 0
        });
      }
      
      if (results[1].status === 'fulfilled') {
        setKnownVendors(results[1].value);
      } else {
        console.error('Known vendors fetch failed:', results[1].reason);
        setKnownVendors({
          total_known_vendors: 397,
          vendors: { disaster_restoration: [], cybersecurity: [], business: [] }
        });
      }
      
      if (results[2].status === 'fulfilled') {
        setPendingVendors(results[2].value.pending_vendors || []);
        setVendorStats(results[2].value.stats || null);
      } else {
        console.error('Discovered vendors fetch failed:', results[2].reason);
        setPendingVendors([]);
        setVendorStats(null);
      }
      
      if (results[3].status === 'fulfilled') {
        setLearnedVendors(results[3].value.learned_vendors || []);
      } else {
        console.error('Learned vendors fetch failed:', results[3].reason);
        setLearnedVendors([]);
      }

      if (results[4].status === 'fulfilled') {
        setTierStats(results[4].value);
      } else {
        console.error('Tier stats fetch failed:', results[4].reason);
        setTierStats(null);
      }
    } catch (error) {
      console.error('Error fetching vendor data:', error);
      toast.error('Failed to load vendor data');
    } finally {
      setLoading(false);
    }
  };

  const approveVendor = async (vendorName: string) => {
    if (!selectedCategory) {
      toast.error('Please select a category');
      return;
    }
    
    setApprovalLoading(vendorName);
    try {
      await api.post('/api/v1/vendor-intelligence/discovered/approve', {
        vendor_name: vendorName,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        confidence: 0.85
      });
      toast.success(`Approved: ${vendorName}`);
      setExpandedVendor(null);
      setSelectedCategory('');
      setSelectedSubcategory('');
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error approving vendor:', error);
      toast.error('Failed to approve vendor');
    } finally {
      setApprovalLoading(null);
    }
  };

  const rejectVendor = async (vendorName: string) => {
    setApprovalLoading(vendorName);
    try {
      await api.post('/api/v1/vendor-intelligence/discovered/reject', {
        vendor_name: vendorName
      });
      toast.success(`Rejected: ${vendorName}`);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error rejecting vendor:', error);
      toast.error('Failed to reject vendor');
    } finally {
      setApprovalLoading(null);
    }
  };

  const testExtraction = async () => {
    if (!testDescription.trim()) return;
    
    setTesting(true);
    try {
      const result = await api.post('/api/v1/vendor-intelligence/extract', {
        description: testDescription
      });
      setTestResult(result);
    } catch (error) {
      console.error('Error testing extraction:', error);
    } finally {
      setTesting(false);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Disaster Recovery': 'bg-red-100 text-red-800',
      'Temporary Operations': 'bg-orange-100 text-orange-800',
      'Business Continuity': 'bg-purple-100 text-purple-800',
      'Payroll & Benefits': 'bg-blue-100 text-blue-800',
      'Technology & Software': 'bg-cyan-100 text-cyan-800',
      'Utilities': 'bg-green-100 text-green-800',
      'Food & Beverage Cost': 'bg-yellow-100 text-yellow-800',
      'Insurance': 'bg-indigo-100 text-indigo-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-7 h-7 text-blue-600" />
            Vendor Intelligence Dashboard
          </h1>
          <p className="text-gray-500 mt-1">
            AI-powered vendor detection and classification analytics
          </p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {[
          { id: 'overview', label: 'Overview', icon: PieChart },
          { id: 'pending', label: `Pending Review (${pendingVendors.length})`, icon: Clock, highlight: pendingVendors.length > 0 },
          { id: 'learned', label: `Learned (${learnedVendors.length})`, icon: BookOpen },
          { id: 'known', label: 'Known Vendors', icon: Building2 },
          { id: 'test', label: 'Test Extraction', icon: Search },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'text-blue-600 border-blue-600'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            } ${tab.highlight ? 'relative' : ''}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.highlight && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
            )}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && summary && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Transactions</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.total_transactions.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Vendors Detected</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.vendors_detected.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">High Confidence Rate</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.high_confidence_rate}%</p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Unknown Vendors</p>
                  <p className="text-2xl font-bold text-amber-600">{summary.unknown_vendors_count}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Classification Tiers & Cost Savings - NEW SECTION */}
          {tierStats && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Tier Breakdown */}
              <div className="lg:col-span-2 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6 shadow-lg text-white">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5 text-emerald-400" />
                  Classification Tier Breakdown
                </h3>
                <p className="text-slate-400 text-sm mb-4">
                  How transactions are being classified - local database saves API costs
                </p>
                
                {/* Tier Progress Bars */}
                <div className="space-y-4">
                  {/* Tier 1 - Local DB */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm font-medium">Tier 1: Local Database</span>
                        <span className="text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full">FREE</span>
                      </div>
                      <span className="text-emerald-400 font-bold">{tierStats.tier_breakdown.tier_1_local_db.percentage}%</span>
                    </div>
                    <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-1000"
                        style={{ width: `${tierStats.tier_breakdown.tier_1_local_db.percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {tierStats.tier_breakdown.tier_1_local_db.count.toLocaleString()} transactions • 7,006 vendors in database
                    </p>
                  </div>

                  {/* Tier 2 - Local ML */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Cpu className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-medium">Tier 2: HuggingFace ML</span>
                        <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full">FREE</span>
                      </div>
                      <span className="text-blue-400 font-bold">{tierStats.tier_breakdown.tier_2_local_ml.percentage}%</span>
                    </div>
                    <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-1000"
                        style={{ width: `${tierStats.tier_breakdown.tier_2_local_ml.percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {tierStats.tier_breakdown.tier_2_local_ml.count.toLocaleString()} transactions • Zero-shot classification
                    </p>
                  </div>

                  {/* Tier 3 - Dual AI */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Brain className="w-4 h-4 text-purple-400" />
                        <span className="text-sm font-medium">Tier 3: Claude + GPT</span>
                        <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full">PAID</span>
                      </div>
                      <span className="text-purple-400 font-bold">{tierStats.tier_breakdown.tier_3_dual_ai.percentage}%</span>
                    </div>
                    <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full transition-all duration-1000"
                        style={{ width: `${tierStats.tier_breakdown.tier_3_dual_ai.percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {tierStats.tier_breakdown.tier_3_dual_ai.count.toLocaleString()} transactions • ${tierStats.tier_breakdown.tier_3_dual_ai.cost.toFixed(2)} API cost
                    </p>
                  </div>
                </div>
              </div>

              {/* Cost Savings Card */}
              <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-xl p-6 shadow-lg text-white">
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                  <TrendingDown className="w-5 h-5" />
                  API Cost Savings
                </h3>
                <p className="text-emerald-100/80 text-sm mb-4">
                  Expanded vendor database reduces expensive API calls
                </p>

                <div className="space-y-4">
                  {/* Savings Rate */}
                  <div className="bg-white/10 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-emerald-100">Savings Rate</span>
                      <span className="text-3xl font-bold">{tierStats.cost_savings.savings_rate}%</span>
                    </div>
                    <p className="text-xs text-emerald-200/60 mt-1">
                      of transactions resolved without API calls
                    </p>
                  </div>

                  {/* Money Saved */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/10 rounded-lg p-3 text-center">
                      <DollarSign className="w-5 h-5 mx-auto mb-1 text-emerald-300" />
                      <p className="text-xl font-bold">${tierStats.cost_savings.total_saved.toFixed(2)}</p>
                      <p className="text-xs text-emerald-200/60">Saved</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold">{tierStats.cost_savings.api_calls_avoided.toLocaleString()}</p>
                      <p className="text-xs text-emerald-200/60">API calls avoided</p>
                    </div>
                  </div>

                  {/* Database Stats */}
                  <div className="pt-3 border-t border-white/20">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-emerald-100/80">Database Size</span>
                      <span className="font-semibold">{tierStats.database_stats.unique_vendors.toLocaleString()} vendors</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-emerald-100/80">Categories</span>
                      <span className="font-semibold">{tierStats.database_stats.categories}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Top Vendors & Disaster Vendors */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Vendors */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Top Vendors by Frequency
              </h3>
              <div className="space-y-3">
                {summary.top_vendors.slice(0, 10).map((vendor, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-400 w-5">{idx + 1}.</span>
                      <span className="font-medium text-gray-900">{vendor.name}</span>
                    </div>
                    <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      {vendor.count} txns
                    </span>
                  </div>
                ))}
                {summary.top_vendors.length === 0 && (
                  <p className="text-gray-500 text-sm">No vendors detected yet</p>
                )}
              </div>
            </div>

            {/* Disaster Recovery Vendors */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Flame className="w-5 h-5 text-red-600" />
                Disaster Recovery Vendors
              </h3>
              <div className="flex flex-wrap gap-2">
                {summary.disaster_vendors.map((vendor, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-sm font-medium border border-red-200"
                  >
                    {vendor}
                  </span>
                ))}
                {summary.disaster_vendors.length === 0 && (
                  <p className="text-gray-500 text-sm">No disaster recovery vendors detected</p>
                )}
              </div>
            </div>
          </div>

          {/* Category Distribution */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-purple-600" />
              Category Distribution
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(summary.category_distribution)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 12)
                .map(([category, count]) => (
                  <div
                    key={category}
                    className={`p-3 rounded-lg ${getCategoryColor(category)}`}
                  >
                    <p className="font-medium text-sm truncate">{category}</p>
                    <p className="text-lg font-bold">{count.toLocaleString()}</p>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Known Vendors Tab */}
      {activeTab === 'known' && knownVendors && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Known Vendor Database
              </h3>
              <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-bold">
                {knownVendors.total_known_vendors} total vendors
              </span>
            </div>

            {/* Expanded Database Stats */}
            {knownVendors.expanded_vendors && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-500" />
                  Expanded Database Breakdown
                </h4>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {Object.entries(knownVendors.expanded_vendors)
                    .filter(([key]) => key !== 'total')
                    .sort((a, b) => b[1] - a[1])
                    .map(([category, count]) => (
                      <div key={category} className="text-center p-2 bg-white rounded-lg shadow-sm">
                        <p className="text-lg font-bold text-indigo-600">{count}</p>
                        <p className="text-xs text-gray-500 capitalize">{category.replace(/_/g, ' ')}</p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Disaster Restoration */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Flame className="w-4 h-4 text-red-500" />
                Disaster Restoration ({knownVendors.vendors.disaster_restoration?.length || 0})
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {knownVendors.vendors.disaster_restoration?.map((vendor, idx) => (
                  <div key={idx} className="p-2 bg-red-50 rounded-lg border border-red-100">
                    <p className="font-medium text-sm text-red-900">{vendor.name}</p>
                    <p className="text-xs text-red-600">{vendor.subcategory}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Cybersecurity */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-purple-500" />
                Cybersecurity ({knownVendors.vendors.cybersecurity?.length || 0})
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {knownVendors.vendors.cybersecurity?.map((vendor, idx) => (
                  <div key={idx} className="p-2 bg-purple-50 rounded-lg border border-purple-100">
                    <p className="font-medium text-sm text-purple-900">{vendor.name}</p>
                    <p className="text-xs text-purple-600">{vendor.subcategory}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Business Vendors */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-500" />
                Business Vendors (showing 30 of {knownVendors.vendors.business?.length || 0})
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {knownVendors.vendors.business?.slice(0, 30).map((vendor, idx) => (
                  <div key={idx} className="p-2 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="font-medium text-xs text-blue-900">{vendor.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Review Tab - NEW */}
      {activeTab === 'pending' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          {vendorStats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Pending Review</p>
                    <p className="text-2xl font-bold text-orange-600">{vendorStats.pending_review}</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Approved</p>
                    <p className="text-2xl font-bold text-green-600">{vendorStats.approved_vendors}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <ThumbsUp className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Rejected</p>
                    <p className="text-2xl font-bold text-gray-500">{vendorStats.rejected_vendors}</p>
                  </div>
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <ThumbsDown className="w-6 h-6 text-gray-500" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pending Vendors List */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Vendors Pending Review
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              These vendors have been detected 3+ times but aren't in the known database. Review and approve to improve future classification accuracy.
            </p>
            
            {pendingVendors.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p className="font-medium">All caught up!</p>
                <p className="text-sm">No vendors pending review.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingVendors.map((vendor) => (
                  <div 
                    key={vendor.name} 
                    className={`border rounded-lg transition-all ${
                      expandedVendor === vendor.name 
                        ? 'border-blue-300 bg-blue-50' 
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    {/* Vendor Header */}
                    <div 
                      className="flex items-center justify-between p-4 cursor-pointer"
                      onClick={() => setExpandedVendor(expandedVendor === vendor.name ? null : vendor.name)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{vendor.name}</p>
                          <p className="text-sm text-gray-500">
                            Seen {vendor.count} times • Last: {new Date(vendor.last_seen).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-1 text-xs bg-orange-100 text-orange-700 rounded-full font-medium">
                          {vendor.count} occurrences
                        </span>
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
                          expandedVendor === vendor.name ? 'rotate-180' : ''
                        }`} />
                      </div>
                    </div>

                    {/* Expanded Approval Form */}
                    {expandedVendor === vendor.name && (
                      <div className="px-4 pb-4 border-t border-blue-200 bg-blue-50/50">
                        <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Category Select */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                            <select
                              value={selectedCategory}
                              onChange={(e) => {
                                setSelectedCategory(e.target.value);
                                setSelectedSubcategory('');
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            >
                              <option value="">Select category...</option>
                              {CATEGORY_OPTIONS.map(cat => (
                                <option key={cat.value} value={cat.value}>{cat.value}</option>
                              ))}
                            </select>
                          </div>

                          {/* Subcategory Select */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
                            <select
                              value={selectedSubcategory}
                              onChange={(e) => setSelectedSubcategory(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                              disabled={!selectedCategory}
                            >
                              <option value="">Select subcategory...</option>
                              {CATEGORY_OPTIONS
                                .find(c => c.value === selectedCategory)
                                ?.subcategories.map(sub => (
                                  <option key={sub} value={sub}>{sub}</option>
                                ))}
                            </select>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-4">
                          <button
                            onClick={() => approveVendor(vendor.name)}
                            disabled={!selectedCategory || approvalLoading === vendor.name}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {approvalLoading === vendor.name ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ThumbsUp className="w-4 h-4" />
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => rejectVendor(vendor.name)}
                            disabled={approvalLoading === vendor.name}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
                          >
                            {approvalLoading === vendor.name ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ThumbsDown className="w-4 h-4" />
                            )}
                            Reject
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Learned Vendors Tab - NEW */}
      {activeTab === 'learned' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-green-600" />
              Admin-Approved Vendors
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              Vendors that have been reviewed and approved by administrators. These are now part of the known vendor database.
            </p>

            {learnedVendors.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No learned vendors yet</p>
                <p className="text-sm">Approve vendors from the Pending Review tab to see them here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Vendor</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Category</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Subcategory</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Approved By</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {learnedVendors.map((vendor, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="font-medium text-gray-900">{vendor.vendor_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getCategoryColor(vendor.category)}`}>
                            {vendor.category}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{vendor.subcategory || '-'}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{vendor.approved_by}</td>
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {vendor.approved_at ? new Date(vendor.approved_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test Extraction Tab */}
      {activeTab === 'test' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-600" />
              Test Vendor NER Extraction
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              Enter a transaction description to test the HuggingFace NER vendor extraction
            </p>
            
            <div className="flex gap-3 mb-6">
              <input
                type="text"
                value={testDescription}
                onChange={(e) => setTestDescription(e.target.value)}
                placeholder="e.g., ACH DEBIT TO SERVPRO OF DOWNTOWN"
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyDown={(e) => e.key === 'Enter' && testExtraction()}
              />
              <button
                onClick={testExtraction}
                disabled={testing || !testDescription.trim()}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {testing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                Extract
              </button>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">Extraction Result</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Vendor Name</p>
                    <p className="font-medium text-lg text-gray-900">{testResult.vendor_name || 'Not detected'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Confidence</p>
                    <p className={`font-medium text-lg ${
                      testResult.confidence >= 0.85 ? 'text-green-600' :
                      testResult.confidence >= 0.6 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {(testResult.confidence * 100).toFixed(0)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Source</p>
                    <p className="font-medium text-gray-900">{testResult.source}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Known Vendor</p>
                    <p className="flex items-center gap-1">
                      {testResult.is_known_vendor ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-green-600 font-medium">Yes</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-500">No</span>
                        </>
                      )}
                    </p>
                  </div>
                  {testResult.vendor_category && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500">Category</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(testResult.vendor_category)}`}>
                        {testResult.vendor_category}
                      </span>
                    </div>
                  )}
                  {testResult.reasoning && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500">Reasoning</p>
                      <p className="text-gray-700">{testResult.reasoning}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Example Descriptions */}
            <div className="mt-6">
              <p className="text-sm text-gray-500 mb-2">Try these examples:</p>
              <div className="flex flex-wrap gap-2">
                {[
                  'ACH DEBIT TO SERVPRO OF DOWNTOWN',
                  'WIRE TRANSFER TO BELFOR RESTORATION',
                  'GUSTO PAYROLL 12/15/2024',
                  'SYSCO FOOD SERVICES INC',
                  'CROWDSTRIKE INCIDENT RESPONSE',
                  'UNKNOWN VENDOR XYZ123'
                ].map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => setTestDescription(example)}
                    className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
